import { Alert, AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import link from 'infra/link'
import { PickupResult } from './OpportunityStore'
import { OpportunitySwooping, SwoopingAction } from './OpportunitySwooping'
import { action, observable, makeObservable, computed } from 'mobx'
import { task, TaskGroup } from 'mobx-task'
import { filter, map } from 'rxjs/operators'
import { Id, Router } from 'types/definitions'
import { toStream, toTaskResultStream } from 'utils/mobx-util'
import { filterNonNil } from 'utils/rx-util'
import { OpportunitiesPort } from './ports'
import OpportunityViewModel from './OpportunityViewModel'
import ConsultationModalViewModel from './views/ConsultationModal/ConsultationModal'
import { GetCurrentMember } from 'iam/SessionStore'
import Consultation from 'jobs/consultations/Consultation'
import Opportunity from './Opportunity'

/**
 * View model for Opportunity Details.
 */
export default class OpportunityDetailsViewModel {
  /**
   * The selected opportunity.
   */
  @observable selected: OpportunityViewModel | null = null

  /**
   * List of booked consultations.
   */
  @observable bookedConsultations: Consultation[] = []

  /**
   * View model for the Vanished dialog.
   */
  vanishedDialog = new ModalViewModel()

  /**
   * Task group for Opportunity application and withdrawal.
   */
  applyWithdrawTasks: TaskGroup<any, any>

  /**
   * Constructor.
   */
  constructor(
    private opportunitiesPort: OpportunitiesPort,
    private newAlert: AlertFactory,
    private router: Router,
    private getCurrentMember: GetCurrentMember,
    private canPickUpOpportunityAfterMemberDataRefresh: (
      opportunity: Opportunity,
    ) => Promise<boolean>,
    private consultationModalViewModel: ConsultationModalViewModel,
  ) {
    makeObservable(this)

    this.applyWithdrawTasks = TaskGroup([
      this.applyForOpportunity,
      this.withdrawOpportunityApplication,
    ])

    const selected$ = toStream(() => this.selected)

    // This deselects the opportunity being viewed if it
    // vanishes (someone else picked it up or it was deleted) and
    // shows the Vanished dialog.
    // We always want this to be running, not just while the Ops view is showing.

    // Server rejected with the `Vanished` error when attempting to pick up the
    // opportunity.
    const opVanishedOnPickup$ = toTaskResultStream(this.pickup).pipe(
      filterNonNil(),
      filter((r) => r === PickupResult.Vanished),
    )
    // Opportunity selection changed.
    const selection$ = selected$.pipe(map((vm) => (vm ? vm.opportunity : null)))
    OpportunitySwooping(
      // Manual trigger stream.
      opVanishedOnPickup$,
      // Op selection change stream.
      selection$,
      // The opportunity events.
      opportunitiesPort.events$,
    ).subscribe(
      action((swoopingAction: SwoopingAction) => {
        switch (swoopingAction) {
          case SwoopingAction.OnlyDeselect:
            this.deselect()
            break
          case SwoopingAction.DeselectAndShowVanished:
            this.deselect()
            this.vanishedDialog.show()
            break
        }
      }),
    )
  }

  /*
   * The current member.
   */
  @computed
  get currentMember() {
    return this.getCurrentMember()
  }

  /*
   * The consultation, if any.
   */
  @computed
  get consultation() {
    return this.selected?.opportunity?.consultation
  }

  /**
   * Indicates whether the apply button can be shown.
   */
  @computed
  get canShowApplyButton() {
    return this.selected && this.selected.opportunity.isDispatchOpportunity
  }

  /**
   * Indicates whether the actor has applied to the opportunity.
   */
  @computed
  get applied() {
    return this.selected && this.selected.opportunity.isCandidate
  }

  /**
   * Selects or unselects an opportunity.
   */
  @action.bound
  select(opportunityViewModel: OpportunityViewModel | null) {
    this.selected = opportunityViewModel
  }

  /**
   * Deselects the currently selected Opportunity.
   */
  @action.bound
  deselect() {
    this.select(null)
  }

  /**
   * Sets the booked consultations for the opportunity.
   */
  @action.bound
  async fetchBookedConsultations() {
    this.bookedConsultations =
      await this.opportunitiesPort.fetchBookedConsultations()
  }

  /**
   * Pick up the selected opportunity. This is the non-dispatch-routing pickup action.
   */
  pickup = task.resolved(async () => {
    const canPickup = await this.canPickUpOpportunityAfterMemberDataRefresh(
      this.selected!.opportunity,
    )
    if (!canPickup) {
      return await this.handlePickupOperation(
        Promise.resolve(PickupResult.MaxConcurrentJobs),
      )
    }

    // Picking up a consultation.
    if (this.consultation) {
      await this.fetchBookedConsultations()

      if (
        !this.consultation.availability ||
        this.consultation.availability.length === 0
      ) {
        return await this.handlePickupOperation(
          this.opportunitiesPort.pickupConsultation(
            this.selected!.opportunity.id!.toString(),
            undefined,
          ),
        )
      }

      const bookedTime =
        await this.consultationModalViewModel.promptForTimeSlots(
          {
            requestedLength: this.consultation.requestedLength,
            availability: this.consultation.availability,
            bookedConsultations: this.bookedConsultations,
            client: this.selected!.opportunity.client.user,
          },
          this.currentMember,
        )

      if (!bookedTime) {
        return
      }

      return await this.handlePickupOperation(
        this.opportunitiesPort.pickupConsultation(
          this.selected!.opportunity.id!.toString(),
          bookedTime,
        ),
      )
    }

    // Picking up a normal job.
    return await this.handlePickupOperation(
      this.opportunitiesPort.pickup(this.selected!.opportunity),
    )
  })

  /**
   * Applies the candidate for the opportunity.
   */
  applyForOpportunity = task.resolved(async () => {
    const canPickup = await this.canPickUpOpportunityAfterMemberDataRefresh(
      this.selected!.opportunity,
    )

    if (!canPickup) {
      await this.handlePickupOperation(
        Promise.resolve(PickupResult.MaxConcurrentJobs),
      )

      return
    }

    await this.opportunitiesPort.applyForOpportunity(this.selected!.opportunity)
  })

  /**
   * Withdraws the candidate's application from the opportunity.
   */
  withdrawOpportunityApplication = task.resolved(async () => {
    await this.opportunitiesPort.withdrawApplication(this.selected!.opportunity)
  })

  /**
   * Provides error handling for any pickup operation.
   *
   * @param pickup
   * @returns
   */
  async handlePickupOperation(pickup: Promise<PickupResult>) {
    const op = this.selected
    if (!op) {
      return
    }

    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying('Hang tight!', `Trying to snag ${op.opportunity.name}...`)
      .show()

    try {
      const result = await pickup
      this.select(null)
      this.handlePickupResult(result, alert, op)
      return result
    } catch (error) {
      console.error(error)
      alert.fromError(error)
    }
  }

  /**
   * Handles the pickup result.
   */
  private handlePickupResult(
    result: PickupResult,
    alert: Alert,
    op: OpportunityViewModel,
  ) {
    switch (result) {
      case PickupResult.Vanished: {
        // The vanish dialog is being managed using Rx, see the activate function.
        alert.dismiss()
        break
      }
      case PickupResult.MaxConcurrentJobs: {
        alert
          .withState(AlertState.Error)
          .saying(
            'Job capacity reached',
            'Close some active jobs to pick up more.',
          )
          .autoDismiss(3000)
        break
      }
      case PickupResult.Success: {
        alert
          .withState(AlertState.Success)
          .saying('Woohoo!', 'You got the job!')
          .autoDismiss(3000)

        this.router.push(link.job(Id.assert(op.opportunity.id)))
        break
      }
    }
  }
}
